import { useCallback, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { HubOption } from 'dorian-shared/types/branch/HubOption';
import type { IconCondition } from 'dorian-shared/types/branch/HubOption';
import { createDefaultIconCondition } from '../hubStepDefaults';

type MemoryIcon = { id: number; label: string };
type OnChange = (changes: Partial<HubOption>) => void;

export function useIconConditions(
  iconConditions: IconCondition[],
  onChange: OnChange,
  memoryIcons: MemoryIcon[],
) {
  const addCondition = useCallback(() => {
    onChange({ iconConditions: [...iconConditions, createDefaultIconCondition()] });
  }, [iconConditions, onChange]);

  const removeCondition = useCallback(
    (uuid: string) => {
      onChange({ iconConditions: iconConditions.filter((c) => c.uuid !== uuid) });
    },
    [iconConditions, onChange],
  );

  const updateCondition = useCallback(
    (uuid: string, updated: IconCondition) => {
      onChange({ iconConditions: iconConditions.map((c) => (c.uuid === uuid ? updated : c)) });
    },
    [iconConditions, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = iconConditions.findIndex((c) => c.uuid === active.id);
        const newIndex = iconConditions.findIndex((c) => c.uuid === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onChange({ iconConditions: arrayMove(iconConditions, oldIndex, newIndex) });
        }
      }
    },
    [iconConditions, onChange],
  );

  const summary = useMemo((): string => {
    if (iconConditions.length === 0) return '';

    const first = iconConditions[0];
    const icon = first.resultIconId != null
      ? memoryIcons.find((i) => i.id === first.resultIconId)
      : null;
    let text = icon ? icon.label : 'override';

    if (iconConditions.length > 1) {
      text += ` + ${iconConditions.length - 1} more`;
    }

    return text;
  }, [iconConditions, memoryIcons]);

  return {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
    summary,
  };
}
